/**
 * Request deduplication utility to prevent duplicate API calls
 * This utility helps prevent duplicate concurrent API calls to the same endpoint
 */

// A map to store pending requests by key
const pendingRequests = new Map();

/**
 * Execute a request function with deduplication
 * If a request with the same key is already in progress, returns the existing promise
 *
 * @param {string} key - Unique key for this request (e.g., "endpoint-params")
 * @param {Function} requestFn - Function that returns a promise for the request
 * @returns {Promise} - Promise that resolves with the request result
 */
export const deduplicatedRequest = async (key, requestFn) => {
  // If a request with this key is already in flight, return the existing promise
  if (pendingRequests.has(key)) {
    console.debug(`Deduplicated request: ${key}`);
    return pendingRequests.get(key);
  }

  // Create new request promise
  const requestPromise = requestFn().finally(() => {
    // Clean up after the request completes (success or failure)
    pendingRequests.delete(key);
  });

  // Store the promise
  pendingRequests.set(key, requestPromise);

  return requestPromise;
};

/**
 * For testing purposes - a way to check if a request is in progress
 * @param {string} key - The request key
 * @returns {boolean} - Whether the request is in progress
 */
export const isRequestPending = (key) => {
  return pendingRequests.has(key);
};

/**
 * For testing purposes - clear all pending requests
 */
export const clearPendingRequests = () => {
  pendingRequests.clear();
};

/**
 * For testing purposes - get count of pending requests
 */
export const getPendingRequestCount = () => {
  return pendingRequests.size;
};
